import { useState } from "react";
import { searchApi } from "../api/client";
import type { SearchResult } from "../types";
import { useNavigate } from "react-router-dom";
import "./SearchView.css";
import { useToast } from "../lib/toast";

type Mode = "hybrid" | "semantic" | "fts";

export default function SearchView() {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [mode, setMode] = useState<Mode>("hybrid");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();

  const run = async () => {
    if (!q.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      let r: SearchResult[];
      if (mode === "semantic") r = await searchApi.semantic(q);
      else if (mode === "fts") r = await searchApi.fts(q);
      else r = await searchApi.search(q);
      setResults(r);
    } catch (e) {
      setResults([]);
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">Search</h2>
          <p className="view-subtitle">
            Full-text + semantic search across all chat history
          </p>
        </div>
      </div>

      <div className="card search-bar-card">
        <div className="row">
          <input
            className="input"
            placeholder="Search messages…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            autoFocus
          />
          <select
            className="select"
            style={{ width: "auto" }}
            value={mode}
            onChange={(e) => setMode(e.target.value as Mode)}
          >
            <option value="hybrid">Hybrid</option>
            <option value="semantic">Semantic</option>
            <option value="fts">Full-text</option>
          </select>
          <button className="btn btn-primary" onClick={run} disabled={loading}>
            {loading ? <span className="spinner" /> : "Search"}
          </button>
        </div>
      </div>

      <div className="search-results">
        {!searched && (
          <div className="empty-state">
            <h3>Search your conversations</h3>
            <p>Enter a query to find messages across all chats.</p>
          </div>
        )}
        {searched && results.length === 0 && !loading && (
          <div className="empty-state">
            <h3>No results</h3>
            <p>Try a different query or search mode.</p>
          </div>
        )}
        {results.map((r, i) => (
          <div
            key={r.id + i}
            className="card search-result"
            onClick={() => navigate(`/chat/${r.chat_id}`)}
          >
            <div className="row-between">
              <span className="tag tag-muted">{r.chat_title || "Untitled chat"}</span>
              <span className="dim" style={{ fontSize: 11 }}>
                {r.role} · {new Date(r.created_at).toLocaleString()}
              </span>
            </div>
            <div className="search-result-content">{r.snippet || r.content}</div>
            {typeof r.similarity === "number" && (
              <div className="dim" style={{ fontSize: 11 }}>
                similarity: {r.similarity.toFixed(3)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}